import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { logout } from '../firebase/auth';
import { db, auth } from '../firebase/config';
import {
  collection, onSnapshot, doc, setDoc, updateDoc, deleteDoc,
  addDoc, serverTimestamp, query, where, getDocs, getDoc, orderBy, increment,
} from 'firebase/firestore';

// ── Constants ─────────────────────────────────────────────────────────────────
const PLANS = [
  { id: 'starter',    label: 'Starter',    price: '$29/mo',  color: '#10b981' },
  { id: 'pro',        label: 'Pro',        price: '$79/mo',  color: '#3b82f6' },
  { id: 'enterprise', label: 'Enterprise', price: 'Custom',  color: '#7c3aed' },
];

const ALL_FEATURES = [
  { key: 'webForms',     label: 'Web Forms',     icon: '📝' },
  { key: 'assessments',  label: 'Assessments',   icon: '📊' },
  { key: 'campaigns',    label: 'Campaigns',     icon: '📣' },
  { key: 'integrations', label: 'Integrations',  icon: '🔗' },
  { key: 'activityLog',  label: 'Activity Log',  icon: '📋' },
  { key: 'roundRobin',   label: 'Round Robin',   icon: '🔄' },
  { key: 'pipeline',     label: 'Pipeline',      icon: '📈' },
];

// Modules gated by super admin — not enabled by default.
// The keys here exactly match the moduleKey values in Sidebar.js nav items.
const CONTROLLABLE_MODULES = [
  { key: 'meetings',    label: 'Meetings',      desc: 'Calendar & meeting scheduler with Google/Zoom/Teams integration', icon: '📅' },
  { key: 'reports',     label: 'Reports',       desc: 'Full analytics — lead sources, conversions, counsellor performance', icon: '📊' },
  { key: 'leaves',      label: 'Leave Manager', desc: 'Employee leave requests, approvals, balance tracking',             icon: '🗓️' },
  { key: 'digitalmedia',label: 'Digital Media', desc: 'Facebook Ads spend, campaign analytics, System User token',        icon: '📡' },
  { key: 'documents',   label: 'Documents',     desc: 'Student document collection, checklists, status tracking',         icon: '📁' },
  { key: 'applications',label: 'Applications',  desc: 'University application pipeline — stages, status, deadlines',      icon: '🎓' },
  { key: 'universities',label: 'Universities',  desc: '200+ university rankings DB with shortlisting tool',               icon: '🏛️' },
  { key: 'assessments', label: 'Assessments',   desc: 'Psychometric + aptitude tests with AI-generated reports',          icon: '🧪' },
  { key: 'workreport',     label: 'Work Report',     desc: 'Daily staff reports, admin remarks, team activity monitoring',                                      icon: '📋' },
  { key: 'counselinghub', label: 'Counseling Hub',  desc: 'Essay management, LOR tracking, profile builder, internships & summer programs — premium counselors', icon: '🎓' },
  { key: 'testprep',     label: 'Test Prep',       desc: 'SAT, ACT, GRE, GMAT, TOEFL, IELTS — classes, test series, score tracking & scheduling',              icon: '📝' },
  { key: 'wamonitor',    label: 'WA Monitor',      desc: 'WhatsApp session monitor — QR management, active connections, message activity dashboard',             icon: '💬' },
  { key: 'subAgents',   label: 'Sub-Agents',      desc: 'Partner sub-agent orgs, universities, student applications & commission tracking — separate from main leads', icon: '🤝' },
];

const DEFAULT_FEATURES = ALL_FEATURES.reduce((a, f) => { a[f.key] = true; return a; }, {});
const DEFAULT_THEME    = { appName: 'Clarion CRM', primary: '#7c3aed' };

const EMPTY_FORM = {
  id: '', name: '', adminEmail: '', adminName: '', plan: 'pro', active: true,
  logoUrl: '', theme: { ...DEFAULT_THEME }, features: { ...DEFAULT_FEATURES },
  enabledModules: [], userCap: 10,
  customDomain: '', whitelabelEnabled: false,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function Badge({ label, color }) {
  return (
    <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: color + '18', color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </span>
  );
}

// ── Company Modal ─────────────────────────────────────────────────────────────
function CompanyModal({ editCo, onClose, onSaved }) {
  const [form,    setForm]    = useState(() => editCo
    ? {
        id: editCo.id, name: editCo.name || '', adminEmail: editCo.adminEmail || '',
        adminName: editCo.adminName || '', plan: editCo.plan || 'pro', active: editCo.active !== false,
        logoUrl: editCo.logoUrl || '', theme: { ...DEFAULT_THEME, ...editCo.theme },
        features: { ...DEFAULT_FEATURES, ...editCo.features },
        enabledModules: editCo.enabledModules || [],
        userCap: editCo.userCap || 10,
        customDomain: editCo.customDomain || '', whitelabelEnabled: editCo.whitelabelEnabled || false,
      }
    : { ...EMPTY_FORM, theme: { ...DEFAULT_THEME }, features: { ...DEFAULT_FEATURES } }
  );
  const [saving, setSaving]  = useState(false);
  const [error,  setError]   = useState('');
  const [copied, setCopied]  = useState(false);

  const set     = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setThm  = (k, v) => setForm(p => ({ ...p, theme: { ...p.theme, [k]: v } }));
  const setFeat = (k, v) => setForm(p => ({ ...p, features: { ...p.features, [k]: v } }));

  // Auto-generate ID from name
  const handleNameChange = (v) => {
    set('name', v);
    if (!editCo) set('id', slugify(v));
  };

  const inviteLink = () => {
    const base = window.location.origin;
    return `${base}?invite=${encodeURIComponent(form.adminEmail)}&company=${form.id}`;
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    if (!form.id.trim()) return setError('Company ID is required.');
    if (!form.name.trim()) return setError('Company name is required.');
    if (!form.adminEmail.trim()) return setError('Admin email is required.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adminEmail)) return setError('Please enter a valid admin email.');
    setSaving(true);
    setError('');
    try {
      const coData = {
        name: form.name, adminEmail: form.adminEmail, adminName: form.adminName,
        plan: form.plan, active: form.active,
        logoUrl: form.logoUrl, theme: form.theme, features: form.features,
        enabledModules: form.enabledModules || [],
        userCap: Number(form.userCap) || 10,
        slug: form.id, updatedAt: serverTimestamp(),
        customDomain: form.customDomain.trim().toLowerCase() || '',
        whitelabelEnabled: form.whitelabelEnabled || false,
      };

      // ── publicBranding lookup doc (publicly readable, used by Login.js) ────
      // Written once per save so the login page can query by domain without auth.
      const domainKey = form.customDomain.trim().toLowerCase();
      if (domainKey && form.whitelabelEnabled) {
        await setDoc(doc(db, 'publicBranding', domainKey), {
          companyId: form.id,
          orgName: form.name,
          tabTitle: form.theme.appName || form.name,
          logoUrl: form.logoUrl || '',
          primaryColor: form.theme.primary || '#7c3aed',
          updatedAt: serverTimestamp(),
        });
      } else if (domainKey && !form.whitelabelEnabled && editCo?.whitelabelEnabled) {
        // White-label was turned off — remove the public doc so the domain no longer shows branding
        const { deleteDoc: delDoc } = await import('firebase/firestore');
        await delDoc(doc(db, 'publicBranding', domainKey));
      }
      // Normalized exactly like AuthContext.js's lookup at first sign-in —
      // an admin-typed email ("John@Co.com") must still match the same
      // person typing "john@co.com" later, or the exact-match Firestore
      // query that links them to this company silently finds nothing.
      const adminEmailNorm = form.adminEmail.trim().toLowerCase();

      if (!editCo) {
        coData.createdAt = serverTimestamp();
        await setDoc(doc(db, 'companies', form.id), coData);
        // Pre-register admin so they get the correct role and company on first
        // sign-in. IMPORTANT: if a users doc already exists for this email
        // (the admin signed in once before, e.g. while testing, leaving a doc
        // with companyId:null), we MUST update it — not skip it. Skipping was
        // the root cause of "Company Not Assigned" on second attempts.
        const existing = await getDocs(query(collection(db, 'users'), where('email', '==', adminEmailNorm)));
        if (existing.empty) {
          // No prior sign-in — create the pre-registration stub
          await addDoc(collection(db, 'users'), {
            email: adminEmailNorm,
            name: form.adminName || form.adminEmail.split('@')[0],
            role: 'admin',
            companyId: form.id,
            preRegistered: true,
            createdAt: serverTimestamp(),
          });
        } else {
          // Doc(s) already exist (stub or real profile with null/wrong companyId).
          // Update ALL of them — this handles: prior failed sign-in, old company,
          // or removed-then-re-added admin. The onSnapshot in AuthContext will
          // fire immediately and they'll be let in without needing the email lookup.
          await Promise.all(existing.docs.map(d =>
            updateDoc(d.ref, {
              companyId: form.id,
              role: 'admin',
              ...(d.data().status === 'removed' ? { status: null, removedAt: null } : {}),
            })
          ));
        }
      } else {
        await updateDoc(doc(db, 'companies', form.id), coData);
        // If the admin email has changed, revoke access from any existing admin(s)
        // for this company whose email no longer matches — this triggers a real-time
        // force-logout for whoever is currently signed in as admin.
        if (editCo.adminEmail && editCo.adminEmail.toLowerCase() !== adminEmailNorm) {
          const oldAdmins = await getDocs(query(
            collection(db, 'users'),
            where('companyId', '==', form.id),
            where('role', '==', 'admin'),
          ));
          await Promise.all(
            oldAdmins.docs
              .filter(d => (d.data().email || '').toLowerCase() !== adminEmailNorm)
              .map(d => updateDoc(d.ref, { status: 'removed', companyId: null, removedAt: serverTimestamp() }))
          );
        }
        // Update existing pre-registration for new admin if found
        const existing = await getDocs(query(collection(db, 'users'), where('email', '==', adminEmailNorm), where('preRegistered', '==', true)));
        if (!existing.empty) {
          await updateDoc(existing.docs[0].ref, { companyId: form.id, role: 'admin', name: form.adminName || form.adminEmail.split('@')[0] });
        } else {
          // Also clear 'removed' status if the new admin was previously removed from this company
          const newAdminDocs = await getDocs(query(collection(db, 'users'), where('email', '==', adminEmailNorm)));
          if (!newAdminDocs.empty) {
            const newAdminDoc = newAdminDocs.docs[0];
            if (newAdminDoc.data().status === 'removed' || !newAdminDoc.data().companyId) {
              await updateDoc(newAdminDoc.ref, { companyId: form.id, role: 'admin', status: null });
            }
          }
        }
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a' }}>{editCo ? 'Edit Company' : 'Add New Company'}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{editCo ? 'Update company settings and features' : 'Create a new client CRM account'}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#94a3b8', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Basic info */}
          <Section title="Company Details" icon="🏢">
            <Grid2>
              <Field label="Company Name *">
                <input className="crm-input" value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="e.g. Apex Education" />
              </Field>
              <Field label="Company ID (URL slug) *">
                <input className="crm-input" value={form.id} onChange={e => set('id', slugify(e.target.value))} placeholder="apex-education" disabled={!!editCo} style={{ background: editCo ? '#f8fafc' : undefined }} />
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Unique identifier — cannot be changed after creation</div>
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Admin Email *">
                <input className="crm-input" type="email" value={form.adminEmail} onChange={e => set('adminEmail', e.target.value)} placeholder="admin@company.com" />
              </Field>
              <Field label="Admin Name">
                <input className="crm-input" value={form.adminName} onChange={e => set('adminName', e.target.value)} placeholder="e.g. John Smith" />
              </Field>
            </Grid2>
          </Section>

          {/* Plan */}
          <Section title="Subscription Plan" icon="⭐">
            <div style={{ display: 'flex', gap: 10 }}>
              {PLANS.map(p => (
                <div key={p.id} onClick={() => set('plan', p.id)}
                  style={{ flex: 1, padding: '14px 12px', borderRadius: 12, border: `2px solid ${form.plan === p.id ? p.color : 'rgba(0,0,0,0.08)'}`, background: form.plan === p.id ? p.color + '0f' : '#fafafa', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: form.plan === p.id ? p.color : '#374151' }}>{p.label}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{p.price}</div>
                </div>
              ))}
            </div>
          </Section>

          {/* Branding */}
          <Section title="Branding" icon="🎨">
            <Grid2>
              <Field label="App Name">
                <input className="crm-input" value={form.theme.appName} onChange={e => setThm('appName', e.target.value)} placeholder="Clarion CRM" />
              </Field>
              <Field label="Primary Color">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.theme.primary || '#7c3aed'} onChange={e => setThm('primary', e.target.value)} style={{ width: 38, height: 38, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 8 }} />
                  <input className="crm-input" value={form.theme.primary} onChange={e => setThm('primary', e.target.value)} placeholder="#7c3aed" style={{ flex: 1 }} />
                </div>
              </Field>
            </Grid2>
            <Field label="Company Logo (optional)">
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {form.logoUrl && (
                  <div style={{ width: 52, height: 52, borderRadius: 10, border: '1.5px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#f8fafc', flexShrink: 0 }}>
                    <img src={form.logoUrl} alt="Logo" style={{ width: 44, height: 44, objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label htmlFor="logo-upload" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9, border: '1.5px dashed rgba(124,58,237,0.35)', background: 'rgba(124,58,237,0.04)', color: '#7c3aed', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    🖼 {form.logoUrl ? 'Change Logo' : 'Upload Logo'}
                  </label>
                  <input id="logo-upload" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 500 * 1024) { alert('Logo must be under 500 KB'); return; }
                    const reader = new FileReader();
                    reader.onload = ev => set('logoUrl', ev.target.result);
                    reader.readAsDataURL(file);
                  }} />
                  {form.logoUrl && (
                    <button type="button" onClick={() => set('logoUrl', '')} style={{ padding: '6px 11px', borderRadius: 7, border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.06)', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>✕ Remove</button>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>PNG, JPG, SVG · max 500 KB · shown on their login page</div>
            </Field>

            {/* White-label domain */}
            <div style={{ marginTop: 4, padding: '14px 16px', borderRadius: 12, background: 'rgba(37,99,235,0.04)', border: '1.5px solid rgba(37,99,235,0.12)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>🌐 White-label Domain</div>
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Custom login URL for this organisation</div>
                </div>
                {/* Toggle */}
                <div onClick={() => set('whitelabelEnabled', !form.whitelabelEnabled)}
                  style={{ width: 44, height: 24, borderRadius: 12, background: form.whitelabelEnabled ? '#2563eb' : '#cbd5e1', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: 3, left: form.whitelabelEnabled ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
                </div>
              </div>
              <Field label="Custom Domain (e.g. crm.theircompany.com)">
                <input
                  className="crm-input"
                  value={form.customDomain}
                  onChange={e => set('customDomain', e.target.value.trim().toLowerCase())}
                  placeholder="crm.theircompany.com"
                  disabled={!form.whitelabelEnabled}
                  style={{ opacity: form.whitelabelEnabled ? 1 : 0.45 }}
                />
              </Field>
              {form.whitelabelEnabled && form.customDomain && (
                <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', fontSize: 11, color: '#065f46', lineHeight: 1.7 }}>
                  <strong>DNS setup for the org's IT team:</strong><br />
                  Add a CNAME record: <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4 }}>{form.customDomain}</code> → <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 4 }}>crm.clarioneducation.in</code><br />
                  Then add <strong>{form.customDomain}</strong> as a custom domain in Firebase Console → Hosting.
                </div>
              )}
            </div>
          </Section>

          {/* Features */}
          <Section title="Enabled Features" icon="🔧">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ALL_FEATURES.map(f => {
                const on = form.features[f.key] !== false;
                return (
                  <div key={f.key} onClick={() => setFeat(f.key, !on)}
                    style={{ padding: '8px 14px', borderRadius: 10, border: `1.5px solid ${on ? '#10b981' : 'rgba(0,0,0,0.1)'}`, background: on ? 'rgba(16,185,129,0.08)' : '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.12s' }}>
                    <span style={{ fontSize: 15 }}>{f.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: on ? '#059669' : '#94a3b8' }}>{f.label}</span>
                    <span style={{ fontSize: 12, color: on ? '#10b981' : '#cbd5e1' }}>{on ? '✓' : '○'}</span>
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Module Access */}
          <Section title="Module Access" icon="🧩">
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
              Choose which pages/features this company can access. Unchecked modules are hidden from the sidebar. Core pages (Leads, Follow-ups, Dashboard, Settings) are always on.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {CONTROLLABLE_MODULES.map(m => {
                const on = (form.enabledModules || []).includes(m.key);
                const toggle = () => {
                  const cur = form.enabledModules || [];
                  set('enabledModules', on ? cur.filter(k => k !== m.key) : [...cur, m.key]);
                };
                return (
                  <div key={m.key} onClick={toggle} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                    borderRadius: 10, cursor: 'pointer', transition: 'all 0.12s',
                    border: `1.5px solid ${on ? 'rgba(124,58,237,0.4)' : 'rgba(0,0,0,0.08)'}`,
                    background: on ? 'rgba(124,58,237,0.06)' : '#f8fafc',
                  }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: 5, border: `2px solid ${on ? '#7c3aed' : '#cbd5e1'}`,
                      background: on ? '#7c3aed' : 'transparent', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1,
                    }}>
                      {on && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: on ? '#5b21b6' : '#374151', display: 'flex', gap: 5, alignItems: 'center' }}>
                        <span>{m.icon}</span><span>{m.label}</span>
                      </div>
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1, lineHeight: 1.4 }}>{m.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={() => set('enabledModules', CONTROLLABLE_MODULES.map(m => m.key))}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(124,58,237,0.3)', background: 'rgba(124,58,237,0.07)', color: '#7c3aed', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                Enable All
              </button>
              <button onClick={() => set('enabledModules', [])}
                style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: '#f1f5f9', color: '#64748b', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                Clear All
              </button>
            </div>
          </Section>

          {/* User Cap */}
          <Section title="User Cap" icon="👥">
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Maximum Team Members</label>
                <input
                  className="crm-input"
                  type="number" min="1" max="500" step="1"
                  value={form.userCap || ''}
                  onChange={e => set('userCap', parseInt(e.target.value, 10) || 1)}
                  placeholder="10"
                />
              </div>
              <div style={{ flex: 2, padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)', fontSize: 12, color: '#92400e' }}>
                <strong>Warning</strong> shown at {Math.round((form.userCap || 10) * 0.8)} users (80%) · <strong>Blocked</strong> at {form.userCap || 10} users (100%). Admin can increase cap anytime to unblock.
              </div>
            </div>
          </Section>

          {/* Status + Invite */}
          <Section title="Status & Invite" icon="🔑">
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Account Status</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[true, false].map(v => (
                    <div key={String(v)} onClick={() => set('active', v)}
                      style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1.5px solid ${form.active === v ? (v ? '#10b981' : '#ef4444') : 'rgba(0,0,0,0.08)'}`, background: form.active === v ? (v ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.06)') : '#f8fafc', cursor: 'pointer', textAlign: 'center', transition: 'all 0.12s' }}>
                      <div style={{ fontSize: 16, marginBottom: 2 }}>{v ? '✅' : '⏸'}</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: form.active === v ? (v ? '#059669' : '#dc2626') : '#94a3b8' }}>{v ? 'Active' : 'Suspended'}</div>
                    </div>
                  ))}
                </div>
              </div>
              {form.id && form.adminEmail && (
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>Admin Invite Link</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ flex: 1, padding: '9px 12px', borderRadius: 9, background: '#f1f5f9', border: '1px solid rgba(0,0,0,0.08)', fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {inviteLink()}
                    </div>
                    <button onClick={copyInvite}
                      style={{ padding: '9px 14px', borderRadius: 9, border: 'none', background: copied ? '#10b981' : '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}>
                      {copied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>Share this link with the admin to sign up. They'll automatically get admin access to this company.</div>
                </div>
              )}
            </div>
          </Section>

          {error && (
            <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 13, color: '#dc2626' }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.12)', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '10px 24px', borderRadius: 10, border: 'none', background: saving ? '#94a3b8' : 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'default' : 'pointer', boxShadow: saving ? 'none' : '0 4px 14px rgba(245,158,11,0.3)' }}>
            {saving ? 'Saving…' : editCo ? '✓ Save Changes' : '⚡ Create Company'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
const Section = ({ title, icon, children }) => (
  <div>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7 }}>
      <span>{icon}</span><span>{title}</span>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
  </div>
);

const Grid2 = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>
);

const Field = ({ label, children }) => (
  <div>
    <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</label>
    {children}
  </div>
);

// ── Company Onboarding Panel ──────────────────────────────────────────────────
function OnboardingPanel({ co }) {
  const [info,    setInfo]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink,  setCopiedLink]  = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [showUsers,    setShowUsers]    = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const snap = await getDocs(query(
          collection(db, 'users'),
          where('companyId', '==', co.id),
        ));
        if (cancelled) return;
        // Double-filter client-side to guarantee strict cross-company isolation
        const users = snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(u => u.companyId === co.id && u.role !== 'superadmin');
        const adminUser     = users.find(u => (u.email || '').toLowerCase() === (co.adminEmail || '').toLowerCase());
        // avatar is only set when a user actually signs in via Firebase Auth (AuthContext sets it).
        // Pre-registration stubs created by SuperAdminPortal do not have an avatar field.
        const adminSignedUp = !!(adminUser?.avatar);
        const activeUsers   = users.filter(u => u.status !== 'removed');
        const teamCount     = activeUsers.filter(u =>
          (u.email || '').toLowerCase() !== (co.adminEmail || '').toLowerCase()
        ).length;
        setCompanyUsers(activeUsers);
        setInfo({ adminSignedUp, adminUser, teamCount, totalUsers: activeUsers.length });
      } catch {
        setInfo({ error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [co.id, co.adminEmail]);

  const inviteLink = `${window.location.origin}?invite=${encodeURIComponent(co.adminEmail || '')}&company=${co.id}`;

  const emailTemplate = [
    `Subject: Your ${co.theme?.appName || 'CRM'} account is ready`,
    '',
    `Hi ${co.adminName || 'there'},`,
    '',
    `Your CRM workspace "${co.name}" is set up and ready. Here\'s how to get started:`,
    '',
    `1. Sign in at: ${window.location.origin}`,
    `   Use your email: ${co.adminEmail}`,
    '',
    '2. Connect your work account (Google or Microsoft)',
    '   Go to Settings → Email & Calendar and click Connect.',
    '   This enables email sync and calendar for your whole team.',
    '',
    '3. Invite your team',
    '   Go to Settings → Team and add your staff members.',
    '',
    'Reply to this email if you have any questions.',
  ].join('\n');

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000);
  };
  const copyEmailTpl = () => {
    navigator.clipboard.writeText(emailTemplate);
    setCopiedEmail(true); setTimeout(() => setCopiedEmail(false), 2000);
  };

  const CheckItem = ({ ok, text, sub }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{
        width: 20, height: 20, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 1,
        background: ok ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
        color: ok ? '#059669' : '#d97706',
      }}>
        {ok ? '✓' : '!'}
      </div>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{text}</div>
        {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ padding: '14px 0 2px', fontSize: 12, color: '#94a3b8' }}>Loading setup status…</div>
  );
  if (info?.error) return (
    <div style={{ padding: '14px 0 2px', fontSize: 12, color: '#ef4444' }}>Could not load — check Firestore rules allow superadmin reads.</div>
  );

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* Setup checklist */}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>Setup Checklist</div>
          <CheckItem
            ok={info.adminSignedUp}
            text={info.adminSignedUp ? `Admin signed in — ${info.adminUser?.name || co.adminEmail}` : 'Admin hasn\'t signed in yet'}
            sub={info.adminSignedUp ? 'Account is active' : 'Send them the invite link below'}
          />
          <CheckItem
            ok={info.teamCount > 0}
            text={info.teamCount > 0 ? `${info.teamCount} team member${info.teamCount !== 1 ? 's' : ''} added` : 'No team members yet'}
            sub={info.teamCount > 0 ? `${info.totalUsers} total active account${info.totalUsers !== 1 ? 's' : ''} in this workspace` : 'Admin can invite from Settings → Team'}
          />
          <CheckItem
            ok={false}
            text="Work account (email & calendar sync)"
            sub="Admin connects Google/Microsoft from Settings → Email & Calendar after signing in"
          />
        </div>

        {/* Quick actions */}
        <div style={{ minWidth: 210 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6 }}>Quick Actions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={copyLink}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: copiedLink ? 'rgba(16,185,129,0.08)' : '#f8fafc', color: copiedLink ? '#059669' : '#374151', fontSize: 11, fontWeight: 600, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
              {copiedLink ? '✅ Copied!' : '🔗 Copy Admin Invite Link'}
            </button>
            <button onClick={copyEmailTpl}
              style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: copiedEmail ? 'rgba(16,185,129,0.08)' : '#f8fafc', color: copiedEmail ? '#059669' : '#374151', fontSize: 11, fontWeight: 600, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
              {copiedEmail ? '✅ Copied!' : '📧 Copy Onboarding Email'}
            </button>
            <button onClick={() => setShowUsers(v => !v)}
              style={{ padding: '7px 12px', borderRadius: 8, border: `1px solid ${showUsers ? 'rgba(59,130,246,0.4)' : 'rgba(0,0,0,0.1)'}`, background: showUsers ? 'rgba(59,130,246,0.08)' : '#f8fafc', color: showUsers ? '#2563eb' : '#374151', fontSize: 11, fontWeight: 600, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
              {showUsers ? '▲ Hide Users' : `👥 View Users (${info.totalUsers})`}
            </button>
            <div style={{ padding: '7px 10px', borderRadius: 8, background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.12)', fontSize: 10, color: '#7c3aed', lineHeight: 1.6 }}>
              <strong>Note:</strong> Google & Microsoft work account connections require Platform OAuth credentials to be configured first — check the <strong>Platform Integrations</strong> tab.
            </div>
          </div>
        </div>

      </div>

      {/* Per-company user list — strictly filtered to this company only */}
      {showUsers && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px dashed rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
            Users in {co.name} ({companyUsers.length})
          </div>
          {companyUsers.length === 0 ? (
            <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>No active users yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {companyUsers.map(u => {
                const isAdmin = (u.email || '').toLowerCase() === (co.adminEmail || '').toLowerCase();
                return (
                  <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: isAdmin ? 'rgba(124,58,237,0.05)' : '#f8fafc', border: `1px solid ${isAdmin ? 'rgba(124,58,237,0.15)' : 'rgba(0,0,0,0.06)'}` }}>
                    {/* Avatar */}
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: isAdmin ? 'rgba(124,58,237,0.15)' : 'rgba(59,130,246,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0, overflow: 'hidden' }}>
                      {u.avatar ? <img src={u.avatar} alt="" style={{ width: 30, height: 30, objectFit: 'cover' }} /> : (u.name?.[0] || u.email?.[0] || '?').toUpperCase()}
                    </div>
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a', display: 'flex', gap: 6, alignItems: 'center' }}>
                        {u.name || '—'}
                        {isAdmin && <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(124,58,237,0.1)', color: '#7c3aed', fontSize: 10, fontWeight: 700 }}>ADMIN</span>}
                        {u.role && u.role !== 'admin' && <span style={{ padding: '1px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.08)', color: '#3b82f6', fontSize: 10, fontWeight: 600 }}>{u.role}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                    </div>
                    {/* Status */}
                    <div style={{ fontSize: 10, fontWeight: 600, color: u.avatar ? '#059669' : '#d97706', background: u.avatar ? 'rgba(5,150,105,0.08)' : 'rgba(245,158,11,0.08)', padding: '3px 8px', borderRadius: 5, flexShrink: 0 }}>
                      {u.avatar ? 'Active' : u.preRegistered ? 'Pre-reg' : 'Pending'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Company Row ───────────────────────────────────────────────────────────────
function CompanyRow({ co, onEdit, onToggle, onDelete }) {
  const plan  = PLANS.find(p => p.id === co.plan) || PLANS[1];
  const isActive = co.active !== false;
  const [copied,   setCopied]   = useState(false);
  const [expanded, setExpanded] = useState(false);

  const copyInvite = () => {
    const url = `${window.location.origin}?invite=${encodeURIComponent(co.adminEmail || '')}&company=${co.id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid rgba(0,0,0,0.07)', padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'box-shadow 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'}>

      {/* Top row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>

        {/* Avatar */}
        <div style={{ width: 44, height: 44, borderRadius: 12, background: (co.theme?.primary || '#7c3aed') + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
          {co.logoUrl ? <img src={co.logoUrl} alt="" style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 8 }} /> : '🏢'}
        </div>

        {/* Name + meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{co.name}</span>
            <Badge label={plan.label} color={plan.color} />
            <Badge label={isActive ? 'Active' : 'Suspended'} color={isActive ? '#10b981' : '#94a3b8'} />
          </div>
          <div style={{ fontSize: 12, color: '#64748b', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>🆔 {co.id}</span>
            <span>📧 {co.adminEmail || 'No admin email'}</span>
            {co.createdAt?.seconds && <span>📅 {new Date(co.createdAt.seconds * 1000).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Feature chips */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 200 }}>
          {ALL_FEATURES.filter(f => co.features?.[f.key] !== false).slice(0, 4).map(f => (
            <span key={f.key} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: 'rgba(124,58,237,0.08)', color: '#7c3aed', fontWeight: 600 }}>{f.icon}</span>
          ))}
          {ALL_FEATURES.filter(f => co.features?.[f.key] !== false).length > 4 && (
            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 5, background: '#f1f5f9', color: '#94a3b8', fontWeight: 600 }}>+{ALL_FEATURES.filter(f => co.features?.[f.key] !== false).length - 4}</span>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button onClick={copyInvite} title="Copy admin invite link"
            style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: copied ? 'rgba(16,185,129,0.1)' : '#f8fafc', color: copied ? '#059669' : '#64748b', fontSize: 12, cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s' }}>
            {copied ? '✓' : '🔗'}
          </button>
          <button onClick={() => onToggle(co)} title={isActive ? 'Suspend' : 'Activate'}
            style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: isActive ? '#f59e0b' : '#10b981', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            {isActive ? '⏸' : '▶'}
          </button>
          <button onClick={() => onEdit(co)}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: '#f8fafc', color: '#374151', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            ✏️ Edit
          </button>
          <button onClick={() => setExpanded(e => !e)} title="Onboarding & setup status"
            style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', background: expanded ? 'rgba(124,58,237,0.08)' : '#f8fafc', color: expanded ? '#7c3aed' : '#64748b', fontSize: 11, cursor: 'pointer', fontWeight: 700, transition: 'all 0.15s' }}>
            {expanded ? '▲' : '▼'}
          </button>
          <button onClick={() => onDelete(co)}
            style={{ padding: '7px 11px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)', background: 'rgba(239,68,68,0.05)', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>
            🗑
          </button>
        </div>
      </div>

      {/* Expandable onboarding panel — no private/credentials data shown */}
      {expanded && <OnboardingPanel co={co} />}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return '—';
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60000)   return 'Just now';
  if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
  if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function fmtDuration(loginTs, logoutTs) {
  if (!loginTs) return '—';
  const start = loginTs.toDate ? loginTs.toDate() : new Date(loginTs);
  const end   = logoutTs ? (logoutTs.toDate ? logoutTs.toDate() : new Date(logoutTs)) : new Date();
  const mins  = Math.floor((end - start) / 60000);
  if (mins < 1)   return '<1 min';
  if (mins < 60)  return mins + ' min';
  return Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm';
}

function fmtTime(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return '—';
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) +
    ' · ' + d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

// ── Platform Analytics panel ──────────────────────────────────────────────────
function PlatformAnalytics({ companies }) {
  const [sessions,        setSessions]        = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null); // companyId
  const [sessionFilter,   setSessionFilter]   = useState('all'); // 'all' | 'today' | 'active'

  // Subscribe to platformUsage (superadmin-only collection)
  useEffect(() => {
    const q = query(collection(db, 'platformUsage'), orderBy('loginAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setSessions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingSessions(false);
    }, () => setLoadingSessions(false));
    return unsub;
  }, []);

  // Today boundary
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);

  const isToday = (ts) => {
    if (!ts) return false;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d >= todayStart;
  };

  // Group sessions by company
  const companyMap = useMemo(() => {
    const map = {};
    sessions.forEach(s => {
      if (!map[s.companyId]) map[s.companyId] = [];
      map[s.companyId].push(s);
    });
    return map;
  }, [sessions]);

  // Per-company summary
  const companySummary = useMemo(() => {
    return companies.map(co => {
      const coSessions = companyMap[co.id] || [];
      const todaySessions  = coSessions.filter(s => isToday(s.loginAt));
      const activeSessions = coSessions.filter(s => s.active);
      const lastSession    = coSessions[0]; // already sorted by loginAt desc
      const uniqueUsers    = new Set(coSessions.filter(s => isToday(s.loginAt)).map(s => s.userId)).size;
      const allPages       = coSessions.flatMap(s => (s.pages || []).map(p => p.p));
      const pageCounts     = {};
      allPages.forEach(p => { pageCounts[p] = (pageCounts[p] || 0) + 1; });
      const topPage        = Object.entries(pageCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
      return { co, coSessions, todaySessions, activeSessions, lastSession, uniqueUsers, topPage };
    }).sort((a, b) => {
      // Sort by last activity
      const aTs = a.lastSession?.lastSeenAt?.seconds || 0;
      const bTs = b.lastSession?.lastSeenAt?.seconds || 0;
      return bTs - aTs;
    });
  }, [companies, companyMap]); // eslint-disable-line

  // Global stats
  const globalActive   = sessions.filter(s => s.active).length;
  const globalToday    = sessions.filter(s => isToday(s.loginAt)).length;
  const uniqueCoToday  = new Set(sessions.filter(s => isToday(s.loginAt)).map(s => s.companyId)).size;

  // Filtered sessions for the drilldown panel
  const drillSessions = useMemo(() => {
    if (!selectedCompany) return [];
    let list = companyMap[selectedCompany] || [];
    if (sessionFilter === 'today')  list = list.filter(s => isToday(s.loginAt));
    if (sessionFilter === 'active') list = list.filter(s => s.active);
    return list;
  }, [selectedCompany, companyMap, sessionFilter]); // eslint-disable-line

  const selectedCo = companies.find(c => c.id === selectedCompany);

  return (
    <div>
      {/* Global stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Active Right Now',    value: globalActive,   color: '#10b981', icon: '🟢' },
          { label: 'Sessions Today',      value: globalToday,    color: '#3b82f6', icon: '📈' },
          { label: 'Companies Active Today', value: uniqueCoToday, color: '#f59e0b', icon: '🏢' },
          { label: 'Total Companies',     value: companies.length, color: '#7c3aed', icon: '🌐' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: `1px solid ${s.color}25`, borderRadius: 14, padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: s.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1 }}>{loadingSessions ? '…' : s.value}</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedCompany ? '320px 1fr' : '1fr', gap: 16 }}>
        {/* Company list */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 13, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            🏢 Company Activity
            {selectedCompany && (
              <button onClick={() => setSelectedCompany(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>✕ Clear</button>
            )}
          </div>
          {loadingSessions ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading activity data…</div>
          ) : companySummary.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No session data yet.<br/>Data appears here once users log in.</div>
          ) : (
            <div style={{ maxHeight: 520, overflowY: 'auto' }}>
              {companySummary.map(({ co, todaySessions, activeSessions, lastSession, uniqueUsers, topPage }) => (
                <div key={co.id} onClick={() => setSelectedCompany(co.id)}
                  style={{
                    padding: '12px 18px', cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.05)',
                    background: selectedCompany === co.id ? 'rgba(124,58,237,0.06)' : 'transparent',
                    borderLeft: selectedCompany === co.id ? '3px solid #7c3aed' : '3px solid transparent',
                    transition: 'all 0.12s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    {/* Active indicator */}
                    {activeSessions.length > 0 && (
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', flexShrink: 0, boxShadow: '0 0 6px #10b981' }} />
                    )}
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', flex: 1 }}>{co.name}</div>
                    {activeSessions.length > 0 && (
                      <span style={{ fontSize: 10, fontWeight: 700, background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 20 }}>
                        {activeSessions.length} online
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#64748b' }}>
                    <span>📅 Today: <strong style={{ color: '#374151' }}>{todaySessions.length}</strong></span>
                    <span>👥 Users: <strong style={{ color: '#374151' }}>{uniqueUsers}</strong></span>
                    <span>🔥 <strong style={{ color: '#374151' }}>{topPage}</strong></span>
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                    Last seen: {lastSession ? fmtTs(lastSession.lastSeenAt) : 'Never'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Session drilldown */}
        {selectedCompany && (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  🔍 {selectedCo?.name || selectedCompany} — Session Log
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  {drillSessions.length} session{drillSessions.length !== 1 ? 's' : ''} shown
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {['all', 'today', 'active'].map(f => (
                  <button key={f} onClick={() => setSessionFilter(f)} style={{
                    padding: '5px 12px', borderRadius: 7, border: '1px solid ' + (sessionFilter === f ? '#7c3aed' : 'rgba(0,0,0,0.1)'),
                    background: sessionFilter === f ? 'rgba(124,58,237,0.08)' : '#fff',
                    color: sessionFilter === f ? '#7c3aed' : '#64748b', fontSize: 11, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                  }}>{f === 'all' ? 'All Time' : f === 'today' ? 'Today' : '🟢 Live'}</button>
                ))}
              </div>
            </div>

            {drillSessions.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No sessions match this filter.</div>
            ) : (
              <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                {/* Header */}
                <div style={{ display: 'grid', gridTemplateColumns: '200px 140px 140px 80px 80px 1fr', gap: 8, padding: '8px 18px', background: '#f8fafc', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  <span>User</span><span>Login</span><span>Last Seen</span><span>Duration</span><span>Pages</span><span>Most Visited</span>
                </div>
                {drillSessions.map(s => {
                  const topPages = (s.pages || []).reduce((acc, pg) => {
                    acc[pg.p] = (acc[pg.p] || 0) + 1; return acc;
                  }, {});
                  const topPageEntry = Object.entries(topPages).sort((a, b) => b[1] - a[1]).slice(0, 3);
                  return (
                    <div key={s.id} style={{ display: 'grid', gridTemplateColumns: '200px 140px 140px 80px 80px 1fr', gap: 8, padding: '11px 18px', borderBottom: '1px solid rgba(0,0,0,0.04)', alignItems: 'center', fontSize: 12 }}>
                      {/* User */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          {s.active && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />}
                          <div>
                            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 12 }}>{s.userName || 'Unknown'}</div>
                            <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.userEmail}</div>
                          </div>
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 700, background: s.userRole === 'admin' ? 'rgba(124,58,237,0.1)' : 'rgba(59,130,246,0.1)', color: s.userRole === 'admin' ? '#7c3aed' : '#3b82f6', padding: '1px 6px', borderRadius: 10, textTransform: 'capitalize' }}>
                          {s.userRole}
                        </span>
                      </div>
                      {/* Login */}
                      <div style={{ fontSize: 11, color: '#374151' }}>{fmtTime(s.loginAt)}</div>
                      {/* Last seen */}
                      <div style={{ fontSize: 11, color: s.active ? '#10b981' : '#64748b', fontWeight: s.active ? 700 : 400 }}>
                        {s.active ? '🟢 Active now' : fmtTs(s.lastSeenAt)}
                      </div>
                      {/* Duration */}
                      <div style={{ fontSize: 11, color: '#374151' }}>{fmtDuration(s.loginAt, s.logoutAt)}</div>
                      {/* Page count */}
                      <div style={{ fontSize: 11, color: '#374151' }}>{(s.pages || []).length} visits</div>
                      {/* Top pages */}
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {topPageEntry.map(([pg, cnt]) => (
                          <span key={pg} style={{ fontSize: 10, background: '#f1f5f9', color: '#374151', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                            {pg} ×{cnt}
                          </span>
                        ))}
                        {topPageEntry.length === 0 && <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── OAuth Apps section ─────────────────────────────────────────────────────────
// Lets the platform operator register ONE shared Google/Microsoft/Zoom app
// so every company can pick "Personal account" mode under Settings →
// Integrations (see CredentialIntegrationDetail.js's personalMode toggle and
// OAUTH_SETUP_GUIDE.md Part 2) — no company needs to register its own
// developer app. Unlike the rest of PlatformSettings, this does NOT read/
// write platformConfig/main directly via the Firestore client SDK — Client
// Secrets are too sensitive for that doc's `allow read: if isAuth()` rule
// (any logged-in employee at any company could read it). Instead this talks
// to backend/src/routes/oauth.js's super-admin-gated /platform-apps routes,
// which store the secrets in platformConfig/oauthSecrets — a doc locked to
// `allow read, write: if false` in firestore.rules (admin-SDK only).
const OAUTH_APP_PROVIDERS = [
  { id: 'google',    label: 'Google',    icon: '🔴', phId: 'xxxx.apps.googleusercontent.com',          phSecret: 'GOCSPX-xxxx' },
  { id: 'microsoft', label: 'Microsoft', icon: '🟦', phId: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',      phSecret: 'xxxxxxxxxxxxxxxxxxxxxxxx' },
  { id: 'zoom',      label: 'Zoom',      icon: '🔵', phId: 'xxxxxxxxxxxxxxxxxxxx',                      phSecret: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
];

function PlatformOAuthAppsSection() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const [status,  setStatus]  = useState({});
  const [drafts,  setDrafts]  = useState({});
  const [loading, setLoading] = useState(true);
  const [busyProvider, setBusyProvider] = useState(null);
  const [savedProvider, setSavedProvider] = useState(null);
  const [err, setErr] = useState('');

  const authedFetch = useCallback(async (path, opts = {}) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error('Not signed in');
    return fetch(`${BACKEND_URL}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
  }, [BACKEND_URL]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await authedFetch('/api/oauth/platform-apps');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load OAuth app status');
      setStatus(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => { load(); }, [load]);

  const setDraft = (provider, key, val) => setDrafts(d => ({ ...d, [provider]: { ...d[provider], [key]: val } }));

  const handleSave = async (provider) => {
    const draft = drafts[provider] || {};
    if (!draft.clientId || !draft.clientSecret) { setErr('Both Client ID and Client Secret are required to save.'); return; }
    setBusyProvider(provider); setErr('');
    try {
      const res = await authedFetch(`/api/oauth/platform-apps/${provider}`, {
        method: 'POST',
        body: JSON.stringify({ clientId: draft.clientId, clientSecret: draft.clientSecret }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to save');
      setDrafts(d => ({ ...d, [provider]: {} }));
      setSavedProvider(provider);
      setTimeout(() => setSavedProvider(null), 3000);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyProvider(null);
    }
  };

  const handleClear = async (provider) => {
    if (!window.confirm(`Remove the shared ${provider} app credentials? Any company using "Personal account" mode for ${provider} will stop working until you add new ones (or until env vars take over, if set).`)) return;
    setBusyProvider(provider); setErr('');
    try {
      const res = await authedFetch(`/api/oauth/platform-apps/${provider}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to remove');
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusyProvider(null);
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', marginBottom: 20, overflow: 'hidden' }}>
      <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(59,130,246,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🔐</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>OAuth Apps — "Sign in with Google/Microsoft/Zoom"</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
            One shared app per provider — every company can pick "Personal account" under Settings → Integrations with zero setup of their own. See OAUTH_SETUP_GUIDE.md for how to register the app itself with each provider.
          </div>
        </div>
      </div>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        {err && (
          <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: 12, color: '#b91c1c' }}>
            ⚠️ {err}
          </div>
        )}
        {loading ? (
          <div style={{ fontSize: 13, color: '#94a3b8' }}>Loading…</div>
        ) : OAUTH_APP_PROVIDERS.map(p => {
          const s = status[p.id] || {};
          const draft = drafts[p.id] || {};
          const configured = s.source === 'firestore' || s.source === 'env';
          return (
            <div key={p.id} style={{ padding: '14px 16px', background: '#fafafa', borderRadius: 12, border: '1px solid #eef0f2' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{p.icon} {p.label}</div>
                {configured ? (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: s.source === 'firestore' ? 'rgba(16,185,129,0.1)' : 'rgba(59,130,246,0.1)', color: s.source === 'firestore' ? '#047857' : '#1d4ed8' }}>
                    {s.source === 'firestore' ? '✓ Configured here' : '✓ Configured via backend env vars'}
                  </span>
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'rgba(148,163,184,0.15)', color: '#64748b' }}>Not set up</span>
                )}
              </div>
              {configured && s.clientId && (
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10, fontFamily: 'monospace' }}>Current Client ID: {s.clientId}</div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input
                  value={draft.clientId ?? ''} onChange={e => setDraft(p.id, 'clientId', e.target.value)}
                  placeholder={p.phId}
                  style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.12)', fontSize: 12.5, fontFamily: 'monospace', background: '#fff', boxSizing: 'border-box' }}
                />
                <input
                  type="password"
                  value={draft.clientSecret ?? ''} onChange={e => setDraft(p.id, 'clientSecret', e.target.value)}
                  placeholder={p.phSecret}
                  style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.12)', fontSize: 12.5, fontFamily: 'monospace', background: '#fff', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={() => handleSave(p.id)} disabled={busyProvider === p.id}
                  style={{ fontSize: 12, fontWeight: 600, padding: '7px 14px', borderRadius: 8, border: 'none', background: savedProvider === p.id ? '#10b981' : '#3b82f6', color: '#fff', cursor: busyProvider === p.id ? 'wait' : 'pointer' }}>
                  {busyProvider === p.id ? 'Saving…' : savedProvider === p.id ? '✓ Saved' : 'Save'}
                </button>
                {s.source === 'firestore' && (
                  <button onClick={() => handleClear(p.id)} disabled={busyProvider === p.id}
                    style={{ fontSize: 12, fontWeight: 600, padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: '#fff', color: '#dc2626', cursor: busyProvider === p.id ? 'wait' : 'pointer' }}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
          Secrets here go straight to the backend and into a Firestore document locked to Admin-SDK-only access — unlike the rest of this Platform Settings page, they're never readable through the browser's Firestore client, not even by you.
        </div>
      </div>
    </div>
  );
}

// ── Platform Settings panel ───────────────────────────────────────────────────
function PlatformSettings() {
  const DEFAULTS = {
    fbAppId: '1728065151881937',
    fbVerifyToken: 'clarion_fb_verify_2024',
    platformName: 'Clarion CRM',
    supportEmail: '',
    backendUrl: '',
    // AI API — platform-level
    aiProvider: 'anthropic',
    aiApiKey: '',
    aiAllowCompanyKey: false, // if true, companies can use their own key
  };
  const [cfg,     setCfg]     = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);

  useEffect(() => {
    getDoc(doc(db, 'platformConfig', 'main')).then(snap => {
      if (snap.exists()) setCfg(c => ({ ...c, ...snap.data() }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const set = (k, v) => { setCfg(p => ({ ...p, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true);
    await setDoc(doc(db, 'platformConfig', 'main'), { ...cfg, updatedAt: new Date().toISOString() });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const webhookUrl = cfg.backendUrl ? `${cfg.backendUrl}/webhook/facebook` : '(set Backend URL above to see full path)';

  const fields = [
    { key: 'platformName',  label: 'Platform Name',          ph: 'Clarion CRM',              icon: '🏷️', hint: 'Shown in email footers and branding' },
    { key: 'fbAppId',       label: 'Facebook App ID',        ph: '1728065151881937',          icon: '📘', hint: 'All companies share this single Meta app — get from Meta Developer App Settings → Basic' },
    { key: 'fbVerifyToken', label: 'FB Webhook Verify Token', ph: 'clarion_fb_verify_2024',   icon: '🔐', hint: 'Must match FB_VERIFY_TOKEN in your backend .env' },
    { key: 'backendUrl',    label: 'Backend API URL',         ph: 'https://api.clarioncrm.com', icon: '🌐', hint: 'Your Node.js backend base URL (no trailing slash)' },
    { key: 'supportEmail',  label: 'Support Email',           ph: 'support@clarioncrm.com',   icon: '📧', hint: 'Shown in client-facing error messages' },
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Section: Meta / Facebook */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(24,119,242,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📘</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Meta / Facebook Integration</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>One app for all companies — submit for Meta App Review once</div>
          </div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {fields.slice(1, 3).map(f => (
            <div key={f.key}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{f.icon} {f.label}</label>
              <input
                value={cfg[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder={f.ph}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.12)', fontSize: 13, color: '#0f172a', fontFamily: 'monospace', boxSizing: 'border-box', outline: 'none', background: '#fafafa' }}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>{f.hint}</div>
            </div>
          ))}

          {/* Webhook info box */}
          <div style={{ background: '#f0f9ff', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0369a1', marginBottom: 8 }}>📡 Webhook URL for Meta App</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, fontSize: 11, color: '#0c4a6e', wordBreak: 'break-all' }}>{webhookUrl}</code>
              {cfg.backendUrl && <button onClick={() => navigator.clipboard.writeText(webhookUrl)} style={{ flexShrink: 0, fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(14,165,233,0.3)', background: 'rgba(14,165,233,0.08)', color: '#0369a1', cursor: 'pointer' }}>Copy</button>}
            </div>
            <div style={{ fontSize: 11, color: '#0369a1', marginTop: 8, lineHeight: 1.6 }}>
              In Meta App Dashboard → Webhooks → Subscribe to <strong>leadgen</strong> field, paste this URL + the verify token above.
            </div>
          </div>

          {/* Required permissions box */}
          <div style={{ background: '#faf5ff', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9', marginBottom: 8 }}>🔐 Required Meta Permissions (submit for App Review)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {['leads_retrieval','pages_manage_ads','pages_read_engagement','pages_show_list','pages_manage_metadata','business_management','ads_read'].map(p => (
                <code key={p} style={{ fontSize: 11, background: 'rgba(124,58,237,0.08)', color: '#6d28d9', padding: '3px 8px', borderRadius: 5 }}>{p}</code>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 8 }}>Add Marketing API product to your Meta app to unlock <code>pages_manage_ads</code>.</div>
          </div>
        </div>
      </div>

      {/* Section: OAuth Apps — Google/Microsoft/Zoom "Personal account" shared apps */}
      <PlatformOAuthAppsSection />

      {/* Section: General */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>⚙️ General Platform Config</div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {[fields[0], fields[3], fields[4]].map(f => (
            <div key={f.key}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{f.icon} {f.label}</label>
              <input
                value={cfg[f.key] || ''} onChange={e => set(f.key, e.target.value)} placeholder={f.ph}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.12)', fontSize: 13, color: '#0f172a', boxSizing: 'border-box', outline: 'none', background: '#fafafa' }}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>{f.hint}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Section: AI API */}
      <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.07)', marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🤖</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>AI API — WhatsApp Bot Engine</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>Your master API key powers all company bots. You control the cost.</div>
          </div>
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* This IS the shareable piece for "WhatsApp bot login" — the
              QR-code WhatsApp connection itself (Settings → WhatsApp, Baileys
              under the hood, see backend/src/whatsapp/manager.js) is tied to
              each counselor's own phone/device per session and can't be
              centralized the same way an OAuth app can; this AI key is what
              actually gets shared across every company's bot. */}
          <div style={{ padding: '10px 14px', background: '#eff6ff', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, fontSize: 11, color: '#1e40af', lineHeight: 1.6 }}>
            ℹ️ This key is the platform-shareable part of "WhatsApp bot login." The QR-code WhatsApp connection itself (Settings → WhatsApp, per counselor) is tied to each person's own phone — that can't be centralized the same way an app credential can, since it's literally pairing their device. What you set here is what every company's bot actually shares.
          </div>
          {/* Provider picker */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>🧠 AI Provider</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {[
                { id: 'anthropic', label: 'Claude (Anthropic)', badge: '⭐ Recommended' },
                { id: 'openai',    label: 'GPT-4o (OpenAI)',    badge: '' },
                { id: 'groq',      label: 'Groq (Llama 3)',     badge: '⚡ Cheapest' },
              ].map(p => (
                <div key={p.id} onClick={() => set('aiProvider', p.id)} style={{
                  flex: 1, padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                  border: cfg.aiProvider === p.id ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                  background: cfg.aiProvider === p.id ? 'rgba(124,58,237,0.04)' : '#fafafa',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{p.label}</div>
                  {p.badge && <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 3 }}>{p.badge}</div>}
                </div>
              ))}
            </div>
          </div>
          {/* API Key */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>🔑 Platform AI API Key</label>
            <input
              type="password"
              value={cfg.aiApiKey || ''} onChange={e => set('aiApiKey', e.target.value)}
              placeholder="sk-ant-... or sk-... or gsk_..."
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.12)', fontSize: 13, boxSizing: 'border-box', fontFamily: 'monospace', background: '#fafafa' }}
            />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 5 }}>
              All companies use this key by default. You pay the AI bill — factor it into your pricing.
            </div>
          </div>
          {/* Allow company override toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Allow companies to use their own AI API key</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                If ON — companies see the AI Engine step in their WA AI Bot settings and can override with their own key.
                If OFF — only your key is used, companies never see the API key field.
              </div>
            </div>
            <div onClick={() => set('aiAllowCompanyKey', !cfg.aiAllowCompanyKey)} style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0, marginLeft: 16,
              background: cfg.aiAllowCompanyKey ? '#7c3aed' : '#e2e8f0', position: 'relative',
            }}>
              <div style={{
                position: 'absolute', top: 3, left: cfg.aiAllowCompanyKey ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
              }} />
            </div>
          </div>
          <div style={{ padding: '12px 16px', background: cfg.aiAllowCompanyKey ? '#fef3c7' : '#f0fdf4', borderRadius: 10, fontSize: 12, color: cfg.aiAllowCompanyKey ? '#92400e' : '#065f46' }}>
            {cfg.aiAllowCompanyKey
              ? '⚠️ Companies can add their own key — their bot calls won\'t use your quota.'
              : '✅ Your key only — you have full cost control across all companies.'}
          </div>
        </div>
      </div>

      {/* Save */}
      <button onClick={handleSave} disabled={saving}
        style={{ padding: '12px 32px', borderRadius: 11, border: 'none', background: saved ? '#10b981' : 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', boxShadow: '0 4px 14px rgba(245,158,11,0.3)', transition: 'background 0.2s' }}>
        {saving ? '⏳ Saving…' : saved ? '✓ Saved!' : '💾 Save Platform Config'}
      </button>
      <div style={{ marginTop: 12, fontSize: 11, color: '#94a3b8' }}>
        Changes take effect immediately — no redeployment needed.
      </div>
    </div>
  );
}

// ── Support Inbox (Super Admin) ───────────────────────────────────────────────
function SupportSection({ currentUser }) {
  const [threads,      setThreads]      = useState([]);
  const [selected,     setSelected]     = useState(null); // companyId of selected thread
  const [messages,     setMessages]     = useState([]);
  const [replyText,    setReplyText]    = useState('');
  const [sending,      setSending]      = useState(false);
  const bottomRef = React.useRef(null);

  // Subscribe to all support threads ordered by latest message
  useEffect(() => {
    const q = query(collection(db, 'supportThreads'), orderBy('lastMessageAt', 'desc'));
    return onSnapshot(q, snap => {
      setThreads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Auto-select first thread
  useEffect(() => {
    if (threads.length > 0 && !selected) setSelected(threads[0].id);
  }, [threads, selected]);

  // Subscribe to messages of selected thread
  useEffect(() => {
    if (!selected) return;
    const q = query(
      collection(db, 'supportThreads', selected, 'messages'),
      orderBy('sentAt', 'asc'),
    );
    return onSnapshot(q, snap => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [selected]);

  // Mark unreadSuperAdmin → 0 when thread is opened
  useEffect(() => {
    if (!selected) return;
    const t = threads.find(t => t.id === selected);
    if (t && (t.unreadSuperAdmin || 0) > 0) {
      updateDoc(doc(db, 'supportThreads', selected), { unreadSuperAdmin: 0 }).catch(() => {});
    }
  }, [selected, threads]);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed || sending || !selected) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'supportThreads', selected, 'messages'), {
        text: trimmed,
        senderType: 'superadmin',
        senderName: currentUser?.name || 'Platform Support',
        sentAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'supportThreads', selected), {
        lastMessageAt:   serverTimestamp(),
        lastMessageText: trimmed.slice(0, 120),
        unreadAdmin:     increment(1),
        unreadSuperAdmin: 0,
        status: 'open',
      });
      setReplyText('');
    } finally {
      setSending(false);
    }
  };

  const selectedThread = threads.find(t => t.id === selected);

  if (threads.length === 0) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320, flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 40 }}>🎧</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>No support requests yet</div>
      <div style={{ fontSize: 13, color: '#94a3b8' }}>When company admins send support messages, they'll appear here.</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 160px)', minHeight: 400 }}>

      {/* Thread list — left column */}
      <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto' }}>
        {threads.map(t => {
          const unread = t.unreadSuperAdmin || 0;
          const isSelected = t.id === selected;
          return (
            <div key={t.id} onClick={() => setSelected(t.id)}
              style={{
                padding: '12px 14px', borderRadius: 12, cursor: 'pointer', transition: 'all 0.12s',
                border: isSelected ? '1.5px solid rgba(245,158,11,0.5)' : '1px solid rgba(255,255,255,0.08)',
                background: isSelected ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.04)',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(245,158,11,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>🏢</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.companyName || t.id}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{t.adminEmail || ''}</div>
                </div>
                {unread > 0 && (
                  <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 20, fontSize: 9, fontWeight: 800, padding: '2px 6px', flexShrink: 0 }}>{unread}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.lastMessageText || 'No messages yet'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                  background: t.status === 'resolved' ? 'rgba(16,185,129,0.15)' : 'rgba(59,130,246,0.15)',
                  color: t.status === 'resolved' ? '#10b981' : '#60a5fa',
                  textTransform: 'uppercase', letterSpacing: 0.4,
                }}>{t.status || 'open'}</span>
                {t.lastMessageAt?.seconds && (
                  <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)' }}>
                    {new Date(t.lastMessageAt.seconds * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversation — right column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.07)' }}>
        {selectedThread ? (
          <>
            {/* Thread header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 12, background: '#fafafa', flexShrink: 0 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{selectedThread.companyName || selectedThread.id}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{selectedThread.adminName} · {selectedThread.adminEmail}</div>
              </div>
              <button
                onClick={() => updateDoc(doc(db, 'supportThreads', selected), {
                  status: selectedThread.status === 'resolved' ? 'open' : 'resolved',
                }).catch(() => {})}
                style={{
                  padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700,
                  background: selectedThread.status === 'resolved' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                  color: selectedThread.status === 'resolved' ? '#059669' : '#d97706',
                }}>
                {selectedThread.status === 'resolved' ? '↺ Reopen' : '✓ Resolve'}
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {messages.map(msg => {
                const isMine = msg.senderType === 'superadmin';
                return (
                  <div key={msg.id} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '75%',
                      background: isMine ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : '#f1f5f9',
                      color: isMine ? '#fff' : '#0f172a',
                      borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      padding: '10px 14px', fontSize: 13, lineHeight: 1.55,
                      boxShadow: isMine ? '0 2px 8px rgba(245,158,11,0.2)' : '0 1px 3px rgba(0,0,0,0.06)',
                    }}>
                      {!isMine && (
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>
                          {msg.senderName || selectedThread.adminName || 'Admin'}
                        </div>
                      )}
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</div>
                      <div style={{ fontSize: 10, marginTop: 5, opacity: 0.55, textAlign: isMine ? 'right' : 'left' }}>
                        {msg.sentAt?.seconds
                          ? new Date(msg.sentAt.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : '…'}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Reply input */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(0,0,0,0.07)', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  placeholder={'Reply to ' + (selectedThread.adminName || selectedThread.companyName) + '…\nEnter to send · Shift+Enter for newline'}
                  rows={2}
                  style={{ flex: 1, resize: 'none', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 10, padding: '10px 13px', fontSize: 13, fontFamily: 'inherit', outline: 'none', lineHeight: 1.55, background: '#fafafa' }}
                />
                <button onClick={sendReply} disabled={!replyText.trim() || sending}
                  style={{
                    padding: '10px 16px', borderRadius: 10, border: 'none',
                    background: replyText.trim() && !sending ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : '#e2e8f0',
                    color: replyText.trim() && !sending ? '#fff' : '#94a3b8',
                    fontWeight: 700, cursor: replyText.trim() && !sending ? 'pointer' : 'default',
                    fontSize: 16, flexShrink: 0, transition: 'all 0.15s',
                  }}>
                  {sending ? '…' : '→'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 10, color: '#94a3b8' }}>
            <div style={{ fontSize: 36 }}>💬</div>
            <div style={{ fontSize: 13 }}>Select a thread to view the conversation</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Portal ───────────────────────────────────────────────────────────────
export default function SuperAdminPortal({ currentUser }) {
  const [companies,         setCompanies]         = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [showModal,         setShowModal]          = useState(false);
  const [editCo,            setEditCo]            = useState(null);
  const [search,            setSearch]            = useState('');
  const [planFilter,        setPlanFilter]        = useState('all');
  const [navItem,           setNavItem]           = useState('companies');
  const [toast,             setToast]             = useState('');
  const [supportThreads,    setSupportThreads]    = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'companies'), snap => {
      setCompanies(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
      setLoading(false);
    });
    return unsub;
  }, []);

  // Subscribe to all support threads for unread badge in nav
  useEffect(() => {
    const q = query(collection(db, 'supportThreads'), orderBy('lastMessageAt', 'desc'));
    return onSnapshot(q, snap => {
      setSupportThreads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => {});
  }, []);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }, []);

  const openNew  = () => { setEditCo(null); setShowModal(true); };
  const openEdit = (co) => { setEditCo(co); setShowModal(true); };

  const handleToggle = async (co) => {
    await updateDoc(doc(db, 'companies', co.id), { active: co.active === false });
    showToast(`${co.name} ${co.active === false ? 'activated' : 'suspended'}`);
  };

  const handleDelete = async (co) => {
    if (!window.confirm(`Permanently delete "${co.name}"? This cannot be undone.`)) return;
    await deleteDoc(doc(db, 'companies', co.id));
    showToast(`${co.name} deleted`);
  };

  const filtered = companies.filter(co => {
    const matchSearch = !search || co.name?.toLowerCase().includes(search.toLowerCase()) || co.adminEmail?.toLowerCase().includes(search.toLowerCase()) || co.id?.toLowerCase().includes(search.toLowerCase());
    const matchPlan   = planFilter === 'all' || co.plan === planFilter;
    return matchSearch && matchPlan;
  });

  const stats = {
    total:  companies.length,
    active: companies.filter(c => c.active !== false).length,
    plans:  PLANS.reduce((a, p) => { a[p.id] = companies.filter(c => c.plan === p.id).length; return a; }, {}),
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f8fafc', fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}>

      {/* ── Left Sidebar ── */}
      <div style={{ width: 230, background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        {/* Logo */}
        <div style={{ padding: '22px 20px 18px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 38, height: 38, borderRadius: 11, background: 'linear-gradient(135deg,#f59e0b,#ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, boxShadow: '0 4px 12px rgba(245,158,11,0.4)' }}>⚡</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: -0.3 }}>Clarion CRM</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Super Admin</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {(() => {
            const supportUnread = supportThreads.reduce((n, t) => n + (t.unreadSuperAdmin || 0), 0);
            return [
              { id: 'companies', icon: '🏢', label: 'Companies', count: companies.length },
              { id: 'analytics', icon: '📊', label: 'Analytics' },
              { id: 'platform',  icon: '🔧', label: 'Platform Config' },
              { id: 'support',   icon: '🎧', label: 'Support', badge: supportUnread },
              { id: 'settings',  icon: '⚙️', label: 'Settings' },
            ].map(item => (
              <button key={item.id} onClick={() => setNavItem(item.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', background: navItem === item.id ? 'rgba(245,158,11,0.15)' : 'transparent', color: navItem === item.id ? '#f59e0b' : 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: navItem === item.id ? 700 : 400, width: '100%', textAlign: 'left', transition: 'all 0.12s' }}>
                <span style={{ fontSize: 16 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.count !== undefined && <span style={{ fontSize: 10, fontWeight: 700, background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', padding: '2px 7px', borderRadius: 10 }}>{item.count}</span>}
                {item.badge > 0 && <span style={{ fontSize: 9, fontWeight: 800, background: '#ef4444', color: '#fff', padding: '2px 6px', borderRadius: 10 }}>{item.badge}</span>}
              </button>
            ));
          })()}
        </nav>

        {/* User */}
        <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#f59e0b,#ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
              {(currentUser?.name || currentUser?.email || 'SA').charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.name || 'Super Admin'}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.email || ''}</div>
            </div>
          </div>
          <button onClick={logout}
            style={{ width: '100%', padding: '8px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', fontSize: 12, cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#fca5a5'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Top header */}
        <div style={{ padding: '18px 28px', background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
              {navItem === 'companies' ? '🏢 Companies'
                : navItem === 'analytics' ? '📊 Analytics'
                : navItem === 'platform'  ? '🔧 Platform Config'
                : navItem === 'support'   ? '🎧 Support Inbox'
                : '⚙️ Settings'}
            </h1>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#64748b' }}>
              {navItem === 'companies' ? `${stats.active} active · ${stats.total} total`
                : navItem === 'support' ? `${supportThreads.length} thread${supportThreads.length !== 1 ? 's' : ''} · ${supportThreads.filter(t => t.status !== 'resolved').length} open`
                : ''}
            </p>
          </div>
          <div style={{ flex: 1 }} />
          {navItem === 'companies' && (
            <button onClick={openNew}
              style={{ padding: '10px 20px', borderRadius: 11, border: 'none', background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 4px 14px rgba(245,158,11,0.3)' }}>
              ⚡ Add Company
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
          {navItem === 'companies' && (
            <>
              {/* Stats */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Total Companies', value: stats.total,  color: '#f59e0b', icon: '🏢' },
                  { label: 'Active',          value: stats.active, color: '#10b981', icon: '✅' },
                  { label: 'Starter',         value: stats.plans.starter || 0, color: '#10b981', icon: '🌱' },
                  { label: 'Pro',             value: stats.plans.pro || 0,     color: '#3b82f6', icon: '⭐' },
                  { label: 'Enterprise',      value: stats.plans.enterprise || 0, color: '#7c3aed', icon: '🚀' },
                ].map(s => (
                  <div key={s.label} style={{ flex: 1, background: s.color + '0f', border: `1px solid ${s.color}25`, borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 20 }}>{s.icon}</span>
                    <div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14 }}>🔍</span>
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies, emails, IDs…"
                    style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.1)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['all', ...PLANS.map(p => p.id)].map(p => (
                    <button key={p} onClick={() => setPlanFilter(p)}
                      style={{ padding: '8px 14px', borderRadius: 9, border: '1px solid ' + (planFilter === p ? '#f59e0b' : 'rgba(0,0,0,0.1)'), background: planFilter === p ? 'rgba(245,158,11,0.1)' : '#fff', color: planFilter === p ? '#d97706' : '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                      {p === 'all' ? 'All Plans' : p}
                    </button>
                  ))}
                </div>
              </div>

              {/* List */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 13 }}>Loading companies…</div>
              ) : filtered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60 }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>🏢</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{search ? 'No companies match your search' : 'No companies yet'}</div>
                  <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>{search ? 'Try a different search term' : 'Create your first client CRM account to get started'}</div>
                  {!search && <button onClick={openNew} style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#f59e0b,#ef4444)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>⚡ Add First Company</button>}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {filtered.map(co => (
                    <CompanyRow key={co.id} co={co} onEdit={openEdit} onToggle={handleToggle} onDelete={handleDelete} />
                  ))}
                </div>
              )}
            </>
          )}

          {navItem === 'analytics' && <PlatformAnalytics companies={companies} />}

          {navItem === 'platform' && <PlatformSettings />}

          {navItem === 'support' && <SupportSection currentUser={currentUser} />}

          {navItem === 'settings' && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, border: '1px solid rgba(0,0,0,0.07)' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>⚙️ Super Admin Settings</div>
              <div style={{ fontSize: 13, color: '#64748b', background: '#f8fafc', borderRadius: 10, padding: '14px 16px', lineHeight: 1.7 }}>
                <strong>To create the first super admin account:</strong><br/>
                1. Create a Firebase Auth user (via Firebase Console or the CRM signup page)<br/>
                2. In Firestore → <code>users</code> → find the user document → set <code>role: "superadmin"</code><br/>
                3. Log out and go to <code>yourapp.com/superadmin</code> to access this portal<br/><br/>
                <strong>Portal URL:</strong> <code>{window.location.origin}/superadmin</code>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <CompanyModal
          editCo={editCo}
          onClose={() => setShowModal(false)}
          onSaved={() => showToast(editCo ? 'Company updated ✓' : 'Company created ✓')}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#0f172a', color: '#fff', padding: '12px 20px', borderRadius: 12, fontSize: 13, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.3)', zIndex: 200, animation: 'fadeIn 0.2s ease' }}>
          ✓ {toast}
        </div>
      )}
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
}
